import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import Modal from './Modal';
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';
import './CollageModal.css';

/**
 * Helper to draw text with dynamic font size scaling to fit a width
 */
const drawScaledText = (ctx, text, x, y, maxWidth, initialFontSize, isBold = true, textAlign = 'center') => {
  let fontSize = initialFontSize;
  ctx.font = `${isBold ? 'bold ' : ''}${fontSize}px Arial`;
  
  // Measure and scale down if needed
  while (ctx.measureText(text).width > maxWidth && fontSize > 10) {
    fontSize -= 1;
    ctx.font = `${isBold ? 'bold ' : ''}${fontSize}px Arial`;
  }
  
  const originalAlign = ctx.textAlign;
  ctx.textAlign = textAlign;
  ctx.fillText(text, x, y);
  ctx.textAlign = originalAlign;
  return fontSize;
};

/**
 * Helper to draw an image to fit a rectangle while preserving aspect ratio (object-fit: cover)
 */
const drawImageCover = (ctx, img, x, y, w, h) => {
  if (!img || img.width === 0 || img.height === 0) return;
  
  const imgRatio = img.width / img.height;
  const rectRatio = w / h;
  
  let sx, sy, sWidth, sHeight;
  
  if (imgRatio > rectRatio) {
    // Image is wider than rectangle - crop sides
    sHeight = img.height;
    sWidth = img.height * rectRatio;
    sx = (img.width - sWidth) / 2;
    sy = 0;
  } else {
    // Image is taller than rectangle - crop top/bottom
    sWidth = img.width;
    sHeight = img.width / rectRatio;
    sx = 0;
    sy = (img.height - sHeight) / 2;
  }
  
  ctx.drawImage(img, sx, sy, sWidth, sHeight, x, y, w, h);
};

/**
 * CollageModal component displays a modal with a collage of game images
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.username - Username of the player
 * @param {string} props.overallRank - Player's overall rank in the tournament
 */
function CollageModal({ isOpen, onClose, username, overallRank }) {
  const { theme } = useContext(ThemeContext);
  const { results } = useContext(ResultsContext);
  const config = useTournamentAppConfiguration();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [showScores, setShowScores] = useState(config.showScoresInImagecollage);
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  
  // Special layout for exactly 3 games
  const generateCollageFor3Games = useCallback((ctx, canvas, userGames, images, results, username, overallRank, showScores) => {
    const canvasWidth = 1330;
    
    // Determine aspect ratio from the best game image
    // Fallback to 2.5 if image missing or placeholder
    const bestImg = images[0];
    const defaultRatio = 2.5;
    let bestImgRatio = (bestImg && bestImg.width > 0) ? (bestImg.width / bestImg.height) : defaultRatio;
    
    // Constrain aspect ratio to reasonable bounds (not too skinny or too wide)
    bestImgRatio = Math.max(0.5, Math.min(3.5, bestImgRatio));
    
    // Top Row: 2 images side-by-side (Ranks 2 and 3)
    // Bottom Row: 1 image full width (Rank 1)
    const bottomRowHeight = canvasWidth / bestImgRatio;
    const topRowHeight = (canvasWidth / 2) / bestImgRatio;
    const tagSectionHeight = 50;
    const totalCanvasHeight = topRowHeight + bottomRowHeight + tagSectionHeight;
    
    canvas.width = canvasWidth;
    canvas.height = totalCanvasHeight;
    
    // Fill background
    ctx.fillStyle = '#121212';
    ctx.fillRect(0, 0, canvasWidth, totalCanvasHeight);
    
    // 1. Draw Top Row (Games 2 and 3)
    const topGames = [userGames[1], userGames[2]];
    const topImages = [images[1], images[2]];
    
    topImages.forEach((img, index) => {
      if (!img) return;
      const x = index * (canvasWidth / 2);
      const y = 0;
      const width = canvasWidth / 2;
      const height = topRowHeight;
      
      drawImageCover(ctx, img, x, y, width, height);
      
      // Add semi-transparent overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.fillRect(x, y, width, height);
      
      // Draw rank
      ctx.fillStyle = 'white';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'left';
      const scoreText = showScores ? ` (${topGames[index].score.toLocaleString()})` : '';
      ctx.fillText(`#${topGames[index].rank}${scoreText}`, x + 20, y + height - 20);
      
      // Draw game title with scaling
      drawScaledText(ctx, topGames[index].title, x + 20, y + height - 55, width - 40, 20, false, 'left');
    });
    
    // 2. Draw Bottom Row (Best Game)
    if (images[0]) {
      const x = 0;
      const y = topRowHeight;
      const width = canvasWidth;
      const height = bottomRowHeight;
      const bestGame = userGames[0];
      
      drawImageCover(ctx, images[0], x, y, width, height);
      
      // Highlight Border
      ctx.strokeStyle = '#4CAF50';
      ctx.lineWidth = 14;
      ctx.strokeRect(x + 7, y + 7, width - 14, height - 14);
      
      // Gradient overlay for the best game info
      const gradient = ctx.createLinearGradient(0, y + height - 120, 0, y + height);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.85)');
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y + height - 120, width, 120);
      
      // Best game info
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 10;
      const bestGameText = showScores 
        ? `#${bestGame.rank} - ${bestGame.title} (${bestGame.score.toLocaleString()})`
        : `#${bestGame.rank} - ${bestGame.title}`;
      drawScaledText(ctx, bestGameText, canvasWidth / 2, y + height - 40, width - 60, 54);
      ctx.shadowBlur = 0;
    }
    
    // 3. Tournament Title and User Info
    ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
    ctx.shadowBlur = 12;
    ctx.fillStyle = 'white';
    
    // Title with scaling to prevent cutoff
    drawScaledText(ctx, results.title, canvasWidth / 2, 65, canvasWidth - 100, 46);

    // User info with scaling
    const userDisplayText = config.showOverallRankInImagecollage 
      ? `${username} - ${overallRank || 'Unranked'}`
      : username;
    drawScaledText(ctx, userDisplayText, canvasWidth / 2, 115, canvasWidth - 100, 30);
    
    ctx.shadowBlur = 0;

    // 4. Add Tag Section
    ctx.fillStyle = '#1e1e1e';
    ctx.fillRect(0, topRowHeight + bottomRowHeight, canvasWidth, tagSectionHeight);

    ctx.fillStyle = 'white';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'left';
    const siteUrl = window.location.origin;
    ctx.fillText(`Create your own collage at ${siteUrl}`, 20, topRowHeight + bottomRowHeight + (tagSectionHeight / 2) + 7);
    
    const timestamp = new Date().toLocaleString();
    ctx.textAlign = 'right';
    ctx.font = '18px Arial';
    ctx.fillText(`${timestamp}`, canvasWidth - 20, topRowHeight + bottomRowHeight + (tagSectionHeight / 2) + 7);
  }, [config.showOverallRankInImagecollage]);

  // Generate the collage
  const generateCollage = useCallback(async () => {
    if (!results || !results.games || !username) {
      setError('No game data available or username not provided');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Find games where the user is ranked
      const userGames = [];
      
      results.games.forEach(game => {
        const userEntry = game.leaderboard?.find(entry => 
          entry.userName.toLowerCase() === username.toLowerCase()
        );
        
        if (userEntry) {
          userGames.push({
            title: game.title,
            image: game.image,
            rank: userEntry.rank,
            score: userEntry.score,
            initials: userEntry.initials
          });
        }
      });
      
      if (userGames.length === 0) {
        throw new Error('No ranked games found for this user');
      }
      
      // Sort games by rank (best to worst)
      userGames.sort((a, b) => a.rank - b.rank);
      
      // Get the best ranked game (first in the sorted array)
      const bestGame = userGames[0];
      const userOverallRank = overallRank || 'Unranked';
      
      // Create canvas
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Load all images first
      const loadImage = (url) => {
        return new Promise((resolve, reject) => {
          if (!url) {
            reject(new Error('No image URL provided'));
            return;
          }
          
          const img = new Image();
          img.crossOrigin = 'Anonymous'; // Enable CORS for the image
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
          img.src = url;
        });
      };
      
      // Load all game images
      const imagePromises = userGames.map(game => 
        loadImage(game.image).catch(() => {
          console.error(`Failed to load image for ${game.title}`);
          // Return a placeholder for failed images
          const placeholder = new Image();
          placeholder.width = 300;
          placeholder.height = 200;
          return placeholder;
        })
      );
      
      // Wait for all images to load
      const images = await Promise.all(imagePromises);
      
      // Draw the collage
      if (userGames.length === 3) {
        generateCollageFor3Games(ctx, canvas, userGames, images, results, username, overallRank, showScores);
      } else {
        // Standard Layout
        const canvasWidth = 1330;
        
        // Determine aspect ratio from the best game image
        const bestImg = images[0];
        const defaultRatio = 2.5;
        let bestImgRatio = (bestImg && bestImg.width > 0) ? (bestImg.width / bestImg.height) : defaultRatio;
        
        // Constrain aspect ratio to reasonable bounds
        bestImgRatio = Math.max(0.5, Math.min(3.5, bestImgRatio));
        
        const canvasHeight = canvasWidth / bestImgRatio;
        const tagSectionHeight = 50;
        const totalCanvasHeight = canvasHeight + tagSectionHeight;
        canvas.width = canvasWidth;
        canvas.height = totalCanvasHeight;
        
        // Fill background with theme-appropriate color
        ctx.fillStyle = '#121212';
        ctx.fillRect(0, 0, canvasWidth, totalCanvasHeight);
        
        // First, draw the background games (up to 4 games)
        const otherGames = userGames.slice(1, 5); // Take up to 4 games after the best one
        const otherImages = images.slice(1, 5);
        
        // Draw background games in a grid pattern without gaps
        if (otherGames.length > 0) {
          // Calculate dimensions based on number of background games
          let positions = [];
          let imgWidth, imgHeight;
          
          switch(otherGames.length) {
            case 1:
              // One background image fills the entire background
              positions = [{ x: 0, y: 0 }];
              imgWidth = canvasWidth;
              imgHeight = canvasHeight;
              break;
            case 2:
              // Two background images side by side
              positions = [
                { x: 0, y: 0 },
                { x: canvasWidth / 2, y: 0 }
              ];
              imgWidth = canvasWidth / 2;
              imgHeight = canvasHeight;
              break;
            case 3:
              // Three background images: 1 on top row, 2 on bottom row
              positions = [
                { x: 0, y: 0 },                    // Top (full width)
                { x: 0, y: canvasHeight / 2 },     // Bottom left
                { x: canvasWidth / 2, y: canvasHeight / 2 } // Bottom right
              ];
              // First image is full width, half height
              // Other images are half width, half height
              break;
            default:
              // 4 or more background images: 2x2 grid
              positions = [
                { x: 0, y: 0 },                    // Top left
                { x: canvasWidth / 2, y: 0 },      // Top right
                { x: 0, y: canvasHeight / 2 },     // Bottom left
                { x: canvasWidth / 2, y: canvasHeight / 2 } // Bottom right
              ];
              imgWidth = canvasWidth / 2;
              imgHeight = canvasHeight / 2;
              break;
          }
          
          otherGames.forEach((game, index) => {
            if (index < positions.length) {
              const img = otherImages[index];
              const pos = positions[index];
              
              // Handle special case for 3 images
              let width = imgWidth;
              let height = imgHeight;
              
              if (otherGames.length === 3) {
                if (index === 0) {
                  // First image is full width, half height
                  width = canvasWidth;
                  height = canvasHeight / 2;
                } else {
                  // Other images are half width, half height
                  width = canvasWidth / 2;
                  height = canvasHeight / 2;
                }
              }
              
              // Draw the image
              drawImageCover(ctx, img, pos.x, pos.y, width, height);
              
              // Add semi-transparent overlay
              ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
              ctx.fillRect(pos.x, pos.y, width, height);
              
              // Determine if this is a right-side image (right column of the grid)
              const isRightSide = (pos.x + width) >= canvasWidth - 5 && width < canvasWidth;
              const textPadding = 10;
              const textX = isRightSide ? pos.x + width - textPadding : pos.x + textPadding;
              const textAlignment = isRightSide ? 'right' : 'left';
              
              // Draw rank
               ctx.fillStyle = 'white';
              ctx.font = 'bold 24px Arial';
              ctx.textAlign = textAlignment;
              const scoreText = showScores ? ` (${game.score.toLocaleString()})` : '';
              ctx.fillText(`#${game.rank}${scoreText}`, textX, pos.y + height - 10);
              
              // Draw game title with scaling
              drawScaledText(ctx, game.title, textX, pos.y + height - 35, width - 20, 18, false, textAlignment);
            }
          });
        }
        
        // Draw the best game in the center (larger)
        if (images[0]) {
          // Calculate best game dimensions - slightly smaller than canvas to allow background visibility
          const bestGameWidth = canvasWidth * 0.6;
          const bestGameHeight = canvasHeight * 0.6;
          const centerX = (canvasWidth - bestGameWidth) / 2;
          const centerY = (canvasHeight - bestGameHeight) / 2;
          
          // Draw a highlight border for the best game
          ctx.strokeStyle = '#4CAF50';
          ctx.lineWidth = 10;
          ctx.strokeRect(centerX - 5, centerY - 5, bestGameWidth + 10, bestGameHeight + 10);
          
          // Draw the best game image
          drawImageCover(ctx, images[0], centerX, centerY, bestGameWidth, bestGameHeight);
          
          // Add semi-transparent overlay for text visibility
          ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
          ctx.fillRect(centerX, centerY, bestGameWidth, 60);
          
          // Draw best game info
          ctx.fillStyle = 'white';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
          ctx.shadowBlur = 6;
          const bestGameText = showScores 
            ? `#${bestGame.rank} - ${bestGame.title} (${bestGame.score.toLocaleString()})`
            : `#${bestGame.rank} - ${bestGame.title}`;
          drawScaledText(ctx, bestGameText, canvasWidth / 2, centerY + 40, bestGameWidth - 40, 28);
          ctx.shadowBlur = 0;
        }
        
        // Add tournament title and user info with scaling and shadows
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 8;
        ctx.fillStyle = 'white';
        
        drawScaledText(ctx, results.title, canvasWidth / 2, 50, canvasWidth - 60, 32);
        
        const userDisplayText = config.showOverallRankInImagecollage 
          ? `${username} - ${userOverallRank}`
          : username;
        drawScaledText(ctx, userDisplayText, canvasWidth / 2, 90, canvasWidth - 60, 24);
        
        ctx.shadowBlur = 0;

        // Add dedicated section for the tag at the bottom
        ctx.fillStyle = '#1e1e1e';
        ctx.fillRect(0, canvasHeight, canvasWidth, tagSectionHeight);

        // Add tag in the dedicated section (left-aligned)
        ctx.fillStyle = 'white'; // White text
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'left';
        const siteUrl = window.location.origin;
        ctx.fillText(`Create your own collage at ${siteUrl}`, 20, canvasHeight + (tagSectionHeight / 2) + 5);
        
        // Add timestamp on the right side of the bottom bar
        const timestamp = new Date().toLocaleString();
        ctx.textAlign = 'right';
        ctx.font = '18px Arial';
        ctx.fillText(`${timestamp}`, canvasWidth - 20, canvasHeight + (tagSectionHeight / 2) + 5);
      }

      // Convert canvas to image
      const dataUrl = canvas.toDataURL('image/png');
      
      // Use a small timeout to ensure the DOM is ready before setting the image source
      setTimeout(() => {
        if (imageRef.current) {
          imageRef.current.src = dataUrl;
        }
      }, 1); // Without this, the image doesn't load
      
      // Create share message
      const baseUrl = window.location.origin + window.location.pathname;
      const directLink = `${baseUrl}?username=${encodeURIComponent(username)}&tournamentId=${encodeURIComponent(results.tournamentId)}`;
      const shareText = config.showOverallRankInImagecollage
        ? `Check out my ${results.title} results! ${username} - ${userOverallRank} \n${directLink}`
        : `Check out my ${results.title} results! ${username} \n${directLink}`;
      setShareMessage(shareText);
      
      setLoading(false);
    } catch (error) {
      console.error('Error generating collage:', error);
      setError(error.message || 'Failed to generate collage');
      setLoading(false);
    }
  }, [results, username, overallRank, generateCollageFor3Games, config.showOverallRankInImagecollage, showScores]);
  
  // Generate collage when modal is opened
  useEffect(() => {
    if (isOpen && username && results) {
      generateCollage();
    }
  }, [isOpen, username, results, generateCollage]);
  
  // Handle download button click
  const handleDownload = () => {
    if (!imageRef.current || !imageRef.current.src) return;
    
    const link = document.createElement('a');
    link.download = `${username}-tournament-collage.png`;
    link.href = imageRef.current.src;
    link.click();
  };
  
  // Handle copy share message
  const handleCopyShareMessage = () => {
    if (!shareMessage) return;
    
    navigator.clipboard.writeText(shareMessage)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text:', err);
        alert('Failed to copy text. Please try again.');
      });
  };
  
  // Handle social media sharing
  const handleShare = (platform) => {
    if (!shareMessage) return;
    
    let shareUrl;
    const directLink = shareMessage.split('\n')[1];
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(directLink)}&quote=${encodeURIComponent(shareMessage)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
        break;
      case 'discord':
        // Discord doesn't have a direct share URL, but we can open a new window with the text ready to copy
        // This opens Discord's website where users can then paste the message
        shareUrl = `https://discord.com/app`;
        // Copy the share message to clipboard for easy pasting
        navigator.clipboard.writeText(shareMessage)
          .then(() => {
            alert('Share message copied to clipboard. Paste it in Discord!');
          })
          .catch(err => {
            console.error('Failed to copy text:', err);
            alert('Failed to copy text. Please copy the message manually and share it on Discord.');
          });
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Tournament Collage"
      size="large"
    >
      <div className={`collage-container ${theme}`}>
        {loading && (
          <div className="collage-loading">Generating collage...</div>
        )}
        
        {error && (
          <div className="collage-error">{error}</div>
        )}
        
        <canvas 
          ref={canvasRef} 
          style={{ display: 'none' }}
        />
        
        {!loading && !error && (
          <>
            <div className="collage-image-container">
              <img 
                ref={imageRef} 
                alt="Tournament Collage" 
                className="collage-image" 
              />
            </div>
            
            <div className="collage-actions">
              <button 
                className="download-button"
                onClick={handleDownload}
              >
                Download Image
              </button>

              <div className="collage-options">
                <label className="checkbox-container">
                  <input 
                    type="checkbox" 
                    checked={showScores} 
                    onChange={(e) => setShowScores(e.target.checked)} 
                  />
                  <span className="checkbox-label">Show Scores</span>
                </label>
              </div>
            </div>
            
            <div className="social-share-container">
              <h3>Share to Social Media</h3>
              
              <div className="share-message-container">
                <input 
                  type="text" 
                  value={shareMessage} 
                  readOnly 
                  className="share-message-field" 
                  placeholder="Share message will appear here"
                />
                <button 
                  className={`copy-button ${isCopied ? 'copied' : ''}`}
                  onClick={handleCopyShareMessage}
                >
                  {isCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              
              <div className="social-buttons">
                <button 
                  className="social-button facebook-button"
                  onClick={() => handleShare('facebook')}
                  disabled={loading || !!error}
                >
                  <i className="social-icon facebook-icon">f</i>
                  Facebook
                </button>
                
                <button 
                  className="social-button twitter-button"
                  onClick={() => handleShare('twitter')}
                  disabled={loading || !!error}
                >
                  <i className="social-icon twitter-icon">𝕏</i>
                  Twitter
                </button>
                
                <button 
                  className="social-button discord-button"
                  onClick={() => handleShare('discord')}
                  disabled={loading || !!error}
                >
                  <i className="social-icon discord-icon">D</i>
                  Discord
                </button>
                
                <button 
                  className="social-button whatsapp-button"
                  onClick={() => handleShare('whatsapp')}
                  disabled={loading || !!error}
                >
                  <i className="social-icon whatsapp-icon">W</i>
                  WhatsApp
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default CollageModal;